// Minimal handoff utility for AiBotBuilder → AiAgent navigation.
// Stores the payload in sessionStorage so the agent page can pick it up.
const HANDOFF_KEY = 'ai_agent_handoff_payload';
const CONSUMED_KEY = 'ai_agent_handoff_consumed';

// Module-level cache that persists across React Strict Mode remounts.
// In React 18 Strict Mode, components are mounted, unmounted, and remounted.
// Without this cache, the first mount would consume/clear the handoff,
// leaving nothing for the "real" second mount.
let cachedHandoffPayload = null;

// Module-level flag to track if handoff has been consumed.
// This persists across React Strict Mode remounts (unlike React refs).
let handoffConsumed = false;

// Module-level variable to track which conversation has a pending message being sent.
// This prevents handleMessagesHydrated from clearing messages during the race condition
// that occurs when navigating to a new conversation and sending a message simultaneously.
// The race: navigate() → component remounts → hydration clears messages → message lost
let pendingMessageConversationId = null;

// Module-level guard for the pending message sender. The pending message stays
// in sessionStorage for display/flicker protection until send settles, so both
// the route-level direct pickup and hydration path can see it. Only one of them
// may claim it for sending.
let claimedPendingMessageConversationId = null;

// Storage key for pending handoff message (survives component remounts)
const PENDING_MESSAGE_KEY = 'ai_agent_pending_handoff_message';

const safeStorage = () => {
  if (typeof window === 'undefined' || typeof window.sessionStorage === 'undefined') {
    return null;
  }
  return window.sessionStorage;
};

export function setAgentHandoff(payload) {
  try {
    // Reset module-level state when setting a new handoff
    cachedHandoffPayload = null;
    handoffConsumed = false;

    const storage = safeStorage();
    if (!storage) return;
    if (payload === undefined || payload === null) {
      storage.removeItem(HANDOFF_KEY);
      storage.removeItem(CONSUMED_KEY);
      return;
    }
    storage.removeItem(CONSUMED_KEY);
    storage.setItem(HANDOFF_KEY, JSON.stringify(payload));
    console.log('[aiAgentHandoff] Payload stored in sessionStorage');

  } catch (err) {
    // Non-fatal: handoff is best-effort.
    console.warn('[aiAgentHandoff] failed to persist payload', err);
  }
}

// Check if handoff has been consumed (persists across Strict Mode remounts)
export function isHandoffConsumed() {
  if (handoffConsumed) return true;
  try {
    const storage = safeStorage();
    return storage?.getItem(CONSUMED_KEY) === 'true';
  } catch (err) {
    return false;
  }
}

// Mark handoff as consumed (call at start of processing, not after)
export function markHandoffConsumed() {
  handoffConsumed = true;
  try {
    const storage = safeStorage();
    storage?.setItem(CONSUMED_KEY, 'true');
  } catch (err) {
    // ignore
  }
  console.log('[aiAgentHandoff] Handoff marked as consumed');
}

export function resetHandoffConsumed() {
  handoffConsumed = false;
  try {
    const storage = safeStorage();
    storage?.removeItem(CONSUMED_KEY);
  } catch (err) {
    // ignore
  }
  console.log('[aiAgentHandoff] Handoff consumption reset');
}

// Set the conversation ID that has a pending message being sent.
// Call this BEFORE navigate() to protect messages from being cleared by hydration.
export function setPendingMessageConversation(conversationId) {
  pendingMessageConversationId = conversationId;
  claimedPendingMessageConversationId = null;
  console.log('[aiAgentHandoff] 🛡️ Pending message conversation set:', conversationId);
}

// Check if a conversation has a pending message (returns the conversation ID or null)
export function getPendingMessageConversation() {
  return pendingMessageConversationId;
}

// Check if the given conversation ID has a pending message. The module-level
// flag protects the normal in-app route transition, while sessionStorage keeps
// the handoff alive across auth redirects, full remounts, and reloads.
export function hasPendingMessage(conversationId) {
  if (!conversationId) return Boolean(pendingMessageConversationId);
  if (pendingMessageConversationId === conversationId) return true;

  const pendingMessage = getPendingHandoffMessage(conversationId);
  return Boolean(pendingMessage?.prompt);
}

// Clear the pending message protection (call after message is successfully added)
export function clearPendingMessageConversation() {
  if (pendingMessageConversationId) {
    console.log('[aiAgentHandoff] 🛡️ Pending message conversation cleared:', pendingMessageConversationId);
  }
  pendingMessageConversationId = null;
}

// Save the pending handoff message to sessionStorage (survives component remounts)
// Call this BEFORE navigate() so the message can be restored after remount
export function savePendingHandoffMessage(conversationId, prompt, attachments = [], context = null) {
  try {
    const storage = safeStorage();
    if (!storage) return;
    claimedPendingMessageConversationId = null;
    const message = {
      conversationId,
      prompt,
      attachments,
      context,
      timestamp: new Date().toISOString(),
    };
    storage.setItem(PENDING_MESSAGE_KEY, JSON.stringify(message));
    console.log('[aiAgentHandoff] 💾 Pending handoff message saved:', {
      conversationId,
      promptPreview: prompt?.slice(0, 30),
    });
  } catch (err) {
    console.warn('[aiAgentHandoff] Failed to save pending message', err);
  }
}

// Get the pending handoff message (for restoring after remount)
export function getPendingHandoffMessage(conversationId) {
  try {
    const storage = safeStorage();
    if (!storage) return null;
    const raw = storage.getItem(PENDING_MESSAGE_KEY);
    if (!raw) return null;
    const message = JSON.parse(raw);
    // Only return if it matches the requested conversation
    if (message.conversationId !== conversationId) {
      console.log('[aiAgentHandoff] Pending message is for different conversation, ignoring');
      return null;
    }
    console.log('[aiAgentHandoff] 💾 Retrieved pending handoff message:', {
      conversationId,
      promptPreview: message.prompt?.slice(0, 30),
    });
    return message;
  } catch (err) {
    console.warn('[aiAgentHandoff] Failed to read pending message', err);
    return null;
  }
}

export function claimPendingHandoffMessage(conversationId) {
  if (!conversationId) return null;
  if (claimedPendingMessageConversationId === conversationId) {
    console.log('[aiAgentHandoff] 💾 Pending handoff message already claimed:', conversationId);
    return null;
  }

  const message = getPendingHandoffMessage(conversationId);
  if (!message?.prompt) {
    return null;
  }

  claimedPendingMessageConversationId = conversationId;
  console.log('[aiAgentHandoff] 💾 Pending handoff message claimed:', {
    conversationId,
    promptPreview: message.prompt?.slice(0, 30),
  });
  return message;
}

// Clear the pending handoff message (call after it's been restored/sent)
export function clearPendingHandoffMessage() {
  try {
    const storage = safeStorage();
    if (storage) {
      storage.removeItem(PENDING_MESSAGE_KEY);
    }
    console.log('[aiAgentHandoff] 💾 Pending handoff message cleared');
  } catch (err) {
    // ignore
  }
}

export function getAgentHandoff() {
  // If we have a cached payload, return it (handles React Strict Mode remounts)
  if (cachedHandoffPayload !== null) {
    console.log('[aiAgentHandoff] Returning cached payload (Strict Mode resilience)');
    return cachedHandoffPayload;
  }

  try {
    const storage = safeStorage();
    if (!storage) return null;
    const raw = storage.getItem(HANDOFF_KEY);
    const payload = raw ? JSON.parse(raw) : null;

    // Cache the payload for subsequent calls (React Strict Mode double-mount)
    if (payload !== null) {
      cachedHandoffPayload = payload;
      console.log('[aiAgentHandoff] Payload read from sessionStorage and cached');
    }

    return payload;
  } catch (err) {
    console.warn('[aiAgentHandoff] failed to read payload', err);
    return null;
  }
}

export function consumeAgentHandoff() {
  const payload = getAgentHandoff();
  clearAgentHandoff();
  return payload;
}

export function clearAgentHandoff() {
  try {
    const storage = safeStorage();
    if (storage) {
      storage.removeItem(HANDOFF_KEY);
    }
    // Also clear the module-level cache
    cachedHandoffPayload = null;
    console.log('[aiAgentHandoff] Handoff cleared (sessionStorage + cache)');
  } catch (err) {
    // ignore
  }
}
